/** Indian HR calendar helpers — English labels, formatting, gazetted holiday hints. */

export const INDIAN_MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Common Central Govt gazetted holidays (name hints for UI badges). */
export const GAZETTED_HOLIDAY_HINTS = [
  { month: 1, day: 26, names: ["republic day"], label: "National", category: "gazetted" },
  { month: 8, day: 15, names: ["independence day"], label: "National", category: "gazetted" },
  { month: 10, day: 2, names: ["gandhi jayanti", "mahatma gandhi"], label: "National", category: "gazetted" },
  { month: 5, day: 1, names: ["may day", "labour day", "labor day"], label: "Gazetted", category: "gazetted" },
  { month: 12, day: 25, names: ["christmas", "christmas day"], label: "Gazetted", category: "gazetted" },
];

export const ATTENDANCE_STATUS_CONFIG = {
  P: { label: "Present", short: "P", tone: "present", hours: "8h" },
  A: { label: "Absent (LOP)", short: "A", tone: "absent", hours: "0h" },
  HD: { label: "Half Day", short: "HD", tone: "halfday", hours: "4h" },
  L: { label: "Leave", short: "L", tone: "leave", hours: "0h" },
  WO: { label: "Week Off", short: "WO", tone: "weekoff", hours: "0h" },
  H: { label: "Holiday", short: "H", tone: "holiday", hours: "0h" },
};

const pad = (n) => String(n).padStart(2, "0");

export function formatIndianDate(isoDate, { withWeekday = false } = {}) {
  if (!isoDate) return "—";
  const m = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return isoDate;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const parts = d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(withWeekday ? { weekday: "short" } : {}),
  });
  return parts;
}

export function formatMonthYearIndian(monthKey) {
  const [y, mo] = monthKey.split("-").map(Number);
  if (!y || !mo) return monthKey;
  const en = INDIAN_MONTHS_EN[mo - 1] || "";
  return { en, year: y, label: `${en} ${y}` };
}

export function todayIsoLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Fixed-date Indian national & common gazetted holidays (month/day). */
export const DEFAULT_INDIAN_HOLIDAY_TEMPLATES = [
  { code: "IN_REPUBLIC_DAY", name: "Republic Day", month: 1, day: 26, is_optional: false, category: "national" },
  { code: "IN_INDEPENDENCE_DAY", name: "Independence Day", month: 8, day: 15, is_optional: false, category: "national" },
  { code: "IN_GANDHI_JAYANTI", name: "Gandhi Jayanti", month: 10, day: 2, is_optional: false, category: "national" },
  { code: "IN_MAY_DAY", name: "May Day", month: 5, day: 1, is_optional: false, category: "national" },
  { code: "IN_CHRISTMAS", name: "Christmas", month: 12, day: 25, is_optional: false, category: "festival" },
  { code: "IN_HOLI", name: "Holi", month: 3, day: 14, is_optional: true, category: "festival" },
  { code: "IN_DIWALI", name: "Diwali", month: 11, day: 1, is_optional: true, category: "festival" },
  { code: "IN_DUSSEHRA", name: "Dussehra", month: 10, day: 12, is_optional: true, category: "festival" },
  { code: "IN_GOOD_FRIDAY", name: "Good Friday", month: 4, day: 18, is_optional: true, category: "festival" },
];

export function buildDefaultHolidaysForYear(year, catalog = []) {
  const templates = catalog.length
    ? catalog.filter((c) => c.month && c.day).map((c) => ({
        code: c.code,
        name: c.name,
        month: c.month,
        day: c.day,
        is_optional: !!c.is_optional_default,
        category: c.category,
      }))
    : DEFAULT_INDIAN_HOLIDAY_TEMPLATES;

  return templates.map((t) => ({
    setting_id: `default-${t.code}-${year}`,
    holiday_id: null,
    holiday_date: `${year}-${pad(t.month)}-${pad(t.day)}`,
    custom_name: t.name,
    name: t.name,
    is_optional: t.is_optional,
    is_enabled: true,
    is_default_preview: true,
    catalog_code: t.code,
  }));
}

/** Merge API holidays with catalog defaults for a year (saved rows win on same date). */
export function mergeOrgHolidaysWithDefaults(orgRows, year, catalog = []) {
  const saved = orgRows || [];
  const savedDates = new Set(saved.map((r) => String(r.holiday_date || "").slice(0, 10)));
  const defaults = buildDefaultHolidaysForYear(year, catalog).filter(
    (d) => !savedDates.has(d.holiday_date)
  );
  return [...saved, ...defaults].sort((a, b) =>
    String(a.holiday_date).localeCompare(String(b.holiday_date))
  );
}

/** past = already occurred, upcoming = today or future (within view year). */
export function holidayTimingStatus(holidayDate, _viewYear) {
  const today = todayIsoLocal();
  const dateStr = String(holidayDate || "").slice(0, 10);
  const y = Number(dateStr.slice(0, 4));
  if (y < Number(today.slice(0, 4))) return "past";
  if (y > Number(today.slice(0, 4))) return "upcoming";
  if (dateStr < today) return "past";
  return "upcoming";
}

export function isTodayInMonth(monthKey, day) {
  const today = todayIsoLocal();
  return today === `${monthKey}-${pad(day)}`;
}

export function classifyIndianHoliday(holidayDate, holidayName) {
  const m = String(holidayDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { category: "custom", badge: "Holiday" };
  const month = Number(m[2]);
  const day = Number(m[3]);
  const nameLower = String(holidayName || "").toLowerCase();

  for (const hint of GAZETTED_HOLIDAY_HINTS) {
    const dateMatch = hint.month === month && hint.day === day;
    const nameMatch = hint.names.some((n) => nameLower.includes(n));
    if (dateMatch || nameMatch) {
      return { category: hint.category, badge: hint.label };
    }
  }
  return { category: "org", badge: "Org Holiday" };
}

export function groupHolidaysByMonth(rows, year) {
  const groups = {};
  for (const row of rows || []) {
    const m = String(row.holiday_date || row.date || "").match(/^(\d{4})-(\d{2})/);
    if (!m || Number(m[1]) !== year) continue;
    const key = m[2];
    if (!groups[key]) {
      groups[key] = {
        monthNum: Number(key),
        monthName: INDIAN_MONTHS_EN[Number(key) - 1],
        items: [],
      };
    }
    groups[key].items.push(row);
  }
  return Object.values(groups).sort((a, b) => a.monthNum - b.monthNum);
}

export function holidayStats(rows) {
  const list = rows || [];
  const mandatory = list.filter((r) => !r.is_optional && (r.is_enabled !== false)).length;
  const optional = list.filter((r) => r.is_optional && (r.is_enabled !== false)).length;
  const national = list.filter((r) => {
    const name = r.name || r.custom_name;
    return classifyIndianHoliday(r.holiday_date, name).category === "gazetted";
  }).length;
  return { total: list.length, mandatory, optional, national };
}
